import * as React from 'react';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import {
    BodyMediumRegular,
    FruitfulCustomIcon,
    H5Emphasized,
    MailIcon,
    RadioInputRow,
    SegmentedControl,
    UserIcon,
    View,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../../common/misc';
import { formatFullNameFallbackEmail } from '../../../../../common/utils/userUtils';
import type { User } from '../../../../../user/types/User';
import type { UpdateBankAccountInfo } from '../../../../types/BankAccount';
import { AccountOwnership } from '../../../../types/BankAccount';
import LegacyBankingTerms from '../../../LegacyBankingTerms';
import CreateEditButton from './CreateEditButton';
import InviteJointOwnerSheet from './InviteJointOwnerSheet';

interface OwnershipProps {
    isEditMode: boolean;
    allowJointInvite: boolean;
    onPressSubmit: (updates?: Partial<UpdateBankAccountInfo>) => void;
    accountInfo: UpdateBankAccountInfo;
    associatedUsers: Array<Partial<User>>;
    hideDisclosures?: boolean;
}

const Ownership = ({
    isEditMode,
    allowJointInvite,
    onPressSubmit,
    accountInfo,
    associatedUsers,
    hideDisclosures = false,
}: OwnershipProps) => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [ownership, setOwnership] = useState(AccountOwnership.INDIVIDUAL);
    const [jointEmail, setJointEmail] = useState<string | null>(null);
    const [isInviteSheetOpen, setIsInviteSheetOpen] = useState(false);

    return (
        <>
            <YStack flex={1} justifyContent="space-between">
                <View marginBottom={32}>
                    <H5Emphasized textAlign="center" marginVertical={12}>
                        Finalize your space
                    </H5Emphasized>
                    <BodyMediumRegular
                        textAlign="center"
                        color="$gray1100"
                        marginBottom={36}
                    >
                        Personalize your savings space based on its purpose
                    </BodyMediumRegular>
                    <View alignSelf="center">
                        <FruitfulCustomIcon
                            icon={accountInfo.icon}
                            color={accountInfo.color}
                            size="xl"
                            showShadow
                        />
                    </View>
                    <H5Emphasized textAlign="center" marginTop={24}>
                        {accountInfo.nickname}
                    </H5Emphasized>
                </View>
                <YStack gap={12}>
                    {allowJointInvite && (
                        <SegmentedControl
                            options={[
                                {
                                    label: 'Individual',
                                    value: AccountOwnership.INDIVIDUAL,
                                },
                                {
                                    label: 'Joint',
                                    value: AccountOwnership.JOINT,
                                },
                            ]}
                            selected={ownership}
                            defaultValue={AccountOwnership.INDIVIDUAL}
                            onValueChange={(ownership) => {
                                setOwnership(ownership);
                            }}
                        />
                    )}
                    {ownership === AccountOwnership.JOINT && (
                        <ScrollView
                            showsVerticalScrollIndicator={false}
                            style={{
                                maxHeight: 180,
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            <SelectJointOwner
                                associatedUsers={associatedUsers}
                                jointEmail={jointEmail}
                                onSelectUser={(email) => {
                                    if (jointEmail === email) {
                                        setJointEmail(null);
                                    } else {
                                        setJointEmail(email);
                                    }
                                }}
                                onPressEmailInvite={() => {
                                    setJointEmail(null);
                                    setIsInviteSheetOpen(true);
                                }}
                            />
                        </ScrollView>
                    )}
                    {!hideDisclosures && (
                        <LegacyBankingTerms
                            marginTop={8}
                            termsAccepted={termsAccepted}
                            onPress={() => {
                                setTermsAccepted(!termsAccepted);
                            }}
                        />
                    )}
                    <CreateEditButton
                        text={
                            isEditMode
                                ? 'Save changes'
                                : hideDisclosures
                                  ? 'Fund my account'
                                  : 'Open and fund my account'
                        }
                        disabled={
                            (!hideDisclosures && !termsAccepted) ||
                            (ownership === AccountOwnership.JOINT &&
                                jointEmail == null)
                        }
                        onPress={() => {
                            onPressSubmit({
                                ...(jointEmail != null ? { jointEmail } : {}),
                            });
                        }}
                    />
                </YStack>
            </YStack>
            <InviteJointOwnerSheet
                isOpen={isInviteSheetOpen}
                onClose={(email?: string) => {
                    email != null && setJointEmail(email);
                    setIsInviteSheetOpen(false);
                }}
            />
        </>
    );
};

interface SelectJointOwnerProps {
    associatedUsers: Array<Partial<User>>;
    jointEmail: string | null;
    onSelectUser: (email: string) => void;
    onPressEmailInvite: () => void;
}

const SelectJointOwner = ({
    associatedUsers,
    jointEmail,
    onSelectUser,
    onPressEmailInvite,
}: SelectJointOwnerProps) => {
    const isInvitingNewUser =
        jointEmail != null &&
        !associatedUsers.map(({ email }) => email).includes(jointEmail);

    return (
        <YStack>
            {!isEmpty(associatedUsers) &&
                associatedUsers.map((user) => {
                    const isSelected = user.email === jointEmail;
                    return (
                        <RadioInputRow
                            key={user.id}
                            label={formatFullNameFallbackEmail(
                                user?.firstName,
                                user?.lastName,
                                user?.email,
                            )}
                            isSelected={isSelected}
                            labelIcon={UserIcon}
                            onPress={() => {
                                onSelectUser(user.email as string);
                            }}
                        />
                    );
                })}
            {isInvitingNewUser && (
                <RadioInputRow
                    key={jointEmail}
                    label={jointEmail}
                    isSelected={true}
                    labelIcon={UserIcon}
                    onPress={() => {
                        onSelectUser(jointEmail);
                    }}
                />
            )}
            <RadioInputRow
                key="invite"
                label="Invite via email"
                isSelected={false}
                labelIcon={MailIcon}
                onPress={onPressEmailInvite}
            />
        </YStack>
    );
};

export default Ownership;
